import type { Transport } from './Transport';

/*
 * המימוש השני של `Transport` — WebSocket אמיתי.
 *
 * הוא נכנס לאותו ממשק כמו `MemoryChannel`, ולכן **ה-lockstep לא יודע שהוא קיים**.
 * הממשק מעביר מחרוזות, כלומר שני המימושים מריצים את אותו קידוד ופענוח בדיוק — זו
 * הסיבה שאפשר לבדוק את הפרוטוקול בלי שרת ועדיין לסמוך עליו בשידור חי.
 *
 * שלוש התנהגויות כאן אינן קוסמטיות:
 *
 *   1. **תור יציאה בזמן ניתוק.** ב-lockstep חייבת לצאת הודעה אחת לכל מספר תור;
 *      תור שנבלע כי הסוקט היה סגור לרגע הוא יריב שממתין לנצח להודעה שלא תגיע.
 *      לכן שליחה בזמן ניתוק נצברת ונשלחת בחיבור הבא, ולא נבלעת.
 *   2. **חיבור מחדש עם השהיה עולה.** ניתוק של שנייה בטלפון הוא הנורמה, לא תקלה.
 *      קוד סגירה בטווח 4000-4099 הוא החלטה של השרת ("החדר מלא", "אין חדר כזה")
 *      ולכן **אין בו טעם לנסות שוב** — ניסיון חוזר שם היה לולאה אינסופית.
 *   3. **`ping` שנענה בשרת בלי להעיר אותו.** ה-Durable Object מוגדר עם
 *      auto-response, ולכן ה-`pong` חוזר בלי שהאובייקט יתעורר. זה מה שמחזיק את
 *      החיבור חי בלובי בלי לשלם על זמן ריצה. `pong` **אינו** מגיע למטפלים — הוא
 *      לא הודעת פרוטוקול, ואם היה עובר הוא היה נספר כהודעה פגומה.
 *
 * **מה זה עדיין לא פותר:** חיבור מחדש מחזיר את הצינור, לא את מה שהוחמץ. ה-relay
 * שומר חלון קצר של מסגרות אחרונות ומשחזר אותן, אבל ניתוק ארוך משמעו שהמסגרות
 * שאבדו כבר נפלטו מהחלון — ואז ה-`LockstepSession` יכריז `peerLost`, וזה נכון.
 */

/** מה שאנחנו באמת צריכים מ-WebSocket. קיים כדי שאפשר יהיה לזייף אותו בטסט. */
export interface WebSocketLike {
    send(data: string): void;
    close(code?: number, reason?: string): void;
    onopen: ((event: unknown) => void) | null;
    onmessage: ((event: { readonly data: unknown }) => void) | null;
    onclose: ((event: { readonly code?: number }) => void) | null;
    onerror: ((event: unknown) => void) | null;
}

export type TransportStatus = 'connecting' | 'open' | 'reconnecting' | 'closed';

export interface WebSocketTransportOptions {
    readonly url: string;
    /** יצירת סוקט. מוזרק בטסטים; ברירת המחדל היא `WebSocket` של הדפדפן. */
    readonly factory?: (url: string) => WebSocketLike;
    /** טיימרים מוזרקים כדי שטסט יחזיק את השעון ביד. */
    readonly setTimer?: (fn: () => void, ms: number) => number;
    readonly clearTimer?: (id: number) => void;
    readonly onStatus?: (status: TransportStatus) => void;
}

/** השהיות הניסיון החוזר. האחרונה חוזרת על עצמה עד שנגמרים הניסיונות. */
export const RETRY_DELAYS_MS = [400, 800, 1_600, 3_200, 6_000] as const;

/** אחרי כמה ניסיונות כושלים ברצף מוותרים ומכריזים על התעבורה כסגורה. */
export const MAX_RETRIES = 12;

/** כל כמה זמן נשלח `ping` כשאין תנועה. הלובי יכול לחכות דקות בלי הודעה אחת. */
export const PING_INTERVAL_MS = 25_000;

/**
 * כמה מסגרות מותר לצבור בניתוק.
 *
 * הגבול קיים כדי שניתוק ארוך לא יבלע זיכרון, אבל מספיק גדול כדי לכסות ניתוק של
 * כמה שניות (‏~7 הודעות בשנייה לצד). מעליו אין טעם להמשיך: המשחק ממילא אבוד.
 */
export const OUTBOX_LIMIT = 512;

/** קוד סגירה בטווח הזה הוא החלטה של השרת ולא תקלה — אין טעם לנסות שוב. */
function isFinalCloseCode(code: number | undefined): boolean {
    return code !== undefined && code >= 4000 && code <= 4099;
}

export class WebSocketTransport implements Transport {
    private readonly handlers = new Set<(data: string) => void>();
    private readonly outbox: string[] = [];
    private readonly options: WebSocketTransportOptions;
    private readonly setTimer: (fn: () => void, ms: number) => number;
    private readonly clearTimer: (id: number) => void;

    private socket: WebSocketLike | null = null;
    private isClosed = false;
    private retries = 0;
    private retryTimer = 0;
    private pingTimer = 0;
    private statusValue: TransportStatus = 'connecting';

    constructor(options: WebSocketTransportOptions) {
        this.options = options;
        this.setTimer =
            options.setTimer ?? ((fn, ms) => setTimeout(fn, ms) as unknown as number);
        this.clearTimer = options.clearTimer ?? ((id) => clearTimeout(id));
        this.open();
    }

    get closed(): boolean {
        return this.isClosed;
    }

    get status(): TransportStatus {
        return this.statusValue;
    }

    /** כמה מסגרות ממתינות לחיבור. אבחון בלבד. */
    get pending(): number {
        return this.outbox.length;
    }

    send(data: string): void {
        if (this.isClosed) return;
        const socket = this.socket;
        if (socket && this.statusValue === 'open') {
            socket.send(data);
            return;
        }
        if (this.outbox.length >= OUTBOX_LIMIT) {
            // מכאן אין מה להציל: חור ברצף התורות נועל את היריב בכל מקרה.
            this.close();
            return;
        }
        this.outbox.push(data);
    }

    onMessage(handler: (data: string) => void): () => void {
        this.handlers.add(handler);
        return () => {
            this.handlers.delete(handler);
        };
    }

    close(): void {
        if (this.isClosed) return;
        this.isClosed = true;
        this.stopTimers();
        this.outbox.length = 0;
        this.detach();
        this.socket?.close(1000, 'closed by client');
        this.socket = null;
        this.handlers.clear();
        this.setStatus('closed');
    }

    private open(): void {
        const create = this.options.factory ?? defaultFactory;
        this.setStatus(this.retries === 0 ? 'connecting' : 'reconnecting');

        const socket = create(this.options.url);
        this.socket = socket;

        socket.onopen = () => {
            if (this.socket !== socket) return;
            this.retries = 0;
            this.setStatus('open');
            // הפליטה קודמת לכל השאר: התורות שנצברו בניתוק חייבים לצאת לפי הסדר.
            const queued = this.outbox.splice(0, this.outbox.length);
            for (const frame of queued) socket.send(frame);
            this.schedulePing();
        };

        socket.onmessage = (event) => {
            if (this.socket !== socket) return;
            const data = event.data;
            if (typeof data !== 'string') return;
            // תשובת ה-keepalive אינה הודעת פרוטוקול; העברה שלה הייתה נספרת כזבל.
            if (data === 'pong') return;
            for (const handler of [...this.handlers]) handler(data);
        };

        socket.onerror = () => {
            /*
             * שגיאה ב-WebSocket תמיד גוררת אחריה `close`, ולכן הטיפול היחיד כאן
             * הוא לא לעשות כלום. ניסיון חיבור מחדש בשני המקומות היה מכפיל סוקטים.
             */
        };

        socket.onclose = (event) => {
            if (this.socket !== socket) return;
            this.detach();
            this.socket = null;
            this.stopTimers();
            if (this.isClosed) return;
            if (isFinalCloseCode(event?.code) || this.retries >= MAX_RETRIES) {
                this.isClosed = true;
                this.setStatus('closed');
                return;
            }
            this.setStatus('reconnecting');
            const delay =
                RETRY_DELAYS_MS[Math.min(this.retries, RETRY_DELAYS_MS.length - 1)] ??
                RETRY_DELAYS_MS[RETRY_DELAYS_MS.length - 1] ??
                1_000;
            this.retries++;
            this.retryTimer = this.setTimer(() => {
                this.retryTimer = 0;
                if (!this.isClosed) this.open();
            }, delay);
        };
    }

    private schedulePing(): void {
        if (this.pingTimer) this.clearTimer(this.pingTimer);
        this.pingTimer = this.setTimer(() => {
            this.pingTimer = 0;
            if (this.isClosed || this.statusValue !== 'open') return;
            this.socket?.send('ping');
            this.schedulePing();
        }, PING_INTERVAL_MS);
    }

    private stopTimers(): void {
        if (this.retryTimer) this.clearTimer(this.retryTimer);
        if (this.pingTimer) this.clearTimer(this.pingTimer);
        this.retryTimer = 0;
        this.pingTimer = 0;
    }

    /** מנתק את המטפלים מהסוקט הישן — סוקט נטוש שממשיך לדבר הוא באג שקשה לאתר. */
    private detach(): void {
        const socket = this.socket;
        if (!socket) return;
        socket.onopen = null;
        socket.onmessage = null;
        socket.onclose = null;
        socket.onerror = null;
    }

    private setStatus(next: TransportStatus): void {
        if (this.statusValue === next) return;
        this.statusValue = next;
        this.options.onStatus?.(next);
    }
}

/*
 * ההמרה כאן היא הגבול היחיד בין הטיפוס המצומצם שאנחנו צריכים לבין ה-WebSocket
 * של הדפדפן: החתימות של `onmessage` ושל `onclose` שם מדויקות יותר משלנו, וזו
 * בדיוק הסיבה שהן אינן מתאימות מבנית בכיוון הזה.
 */
function defaultFactory(url: string): WebSocketLike {
    return new WebSocket(url) as unknown as WebSocketLike;
}
